<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSectionPermissions } from 'dashboard/composables/useSectionPermissions';

const { t } = useI18n();

const { canEditSection } = useSectionPermissions();

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'pause', 'resume', 'viewLogs']);

const scheduleSummary = computed(() => {
  const days = (props.campaign.schedule_days || [])
    .sort()
    .map(d => t(`OMNI.WA_CAMPAIGNS.DAYS.${d}`))
    .join(', ');
  return `${days} | ${props.campaign.schedule_start_time} - ${props.campaign.schedule_end_time}`;
});

const statusBadge = computed(() => {
  if (props.campaign.status === 'completed') {
    return {
      label: t('OMNI.WA_CAMPAIGNS.STATUS.COMPLETED'),
      classes: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
      icon: 'i-lucide-check-circle-2',
    };
  }
  if (props.campaign.processing_state === 'dispatching') {
    return {
      label: t('OMNI.WA_CAMPAIGNS.STATUS.DISPATCHING'),
      classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
      icon: 'i-lucide-loader-2 animate-spin',
    };
  }
  if (props.campaign.status === 'active') {
    return {
      label: t('OMNI.WA_CAMPAIGNS.STATUS.ACTIVE'),
      classes: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
      icon: 'i-lucide-circle-check',
    };
  }
  return {
    label: t('OMNI.WA_CAMPAIGNS.STATUS.PAUSED'),
    classes: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
    icon: 'i-lucide-pause-circle',
  };
});

const messageTypeIcon = computed(() => {
  return props.campaign.message_type === 'image_caption'
    ? 'i-lucide-image'
    : 'i-lucide-type';
});

const lastExecutedLabel = computed(() => {
  if (!props.campaign.last_executed_date) {
    return t('OMNI.WA_CAMPAIGNS.CARD.NEVER_EXECUTED');
  }
  return props.campaign.last_executed_date;
});

const nextRunLabel = computed(() => {
  const dates = props.campaign.next_execution_dates;
  if (!dates || dates.length === 0) return '-';
  // Find the first date that is today or in the future
  const today = new Date().toISOString().split('T')[0];
  const next = dates.find(d => d >= today);
  return next || dates[0];
});
</script>

<template>
  <div class="bg-n-solid-2 border border-n-weak rounded-xl p-5 hover:border-n-strong transition-colors">
    <!-- Header Row -->
    <div class="flex items-start justify-between mb-3">
      <div class="flex items-center gap-3 min-w-0">
        <span :class="messageTypeIcon" class="text-xl text-n-slate-10 shrink-0" />
        <div class="min-w-0">
          <h3 class="text-base font-semibold text-n-slate-12 truncate">
            {{ campaign.title }}
          </h3>
          <p class="text-xs text-n-slate-10 truncate">
            {{ campaign.inbox_name }}
          </p>
        </div>
      </div>
      <span
        class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0"
        :class="statusBadge.classes"
      >
        <span :class="statusBadge.icon" class="size-3.5" />
        {{ statusBadge.label }}
      </span>
    </div>

    <!-- Schedule -->
    <div class="flex items-center gap-2 mb-3 text-sm text-n-slate-11">
      <span class="i-lucide-calendar-clock size-4 shrink-0" />
      <span>{{ scheduleSummary }}</span>
    </div>

    <!-- Badges -->
    <div class="flex items-center gap-3 mb-3">
      <div v-if="campaign.ai_rewrite_enabled" class="flex items-center gap-1.5 text-xs text-violet-600 dark:text-violet-400">
        <span class="i-lucide-sparkles size-3.5" />
        <span>AI rewrite</span>
      </div>
      <div v-if="campaign.auto_finish" class="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400">
        <span class="i-lucide-flag-triangle-right size-3.5" />
        <span>{{ t('OMNI.WA_CAMPAIGNS.CARD.AUTO_FINISH') }}</span>
      </div>
    </div>

    <!-- Stats Row -->
    <div class="flex items-center gap-4 mb-4 text-sm">
      <div class="flex items-center gap-1.5 text-n-slate-11">
        <span class="i-lucide-users size-4" />
        <span>{{ campaign.audience_count }} {{ t('OMNI.WA_CAMPAIGNS.CARD.CONTACTS') }}</span>
      </div>
      <div class="flex items-center gap-1.5 text-green-600 dark:text-green-400">
        <span class="i-lucide-check-circle size-4" />
        <span>{{ campaign.sent_today }}/{{ campaign.daily_limit }} {{ t('OMNI.WA_CAMPAIGNS.CARD.SENT_TODAY') }}</span>
      </div>
      <div v-if="campaign.failed_today > 0" class="flex items-center gap-1.5 text-red-600 dark:text-red-400">
        <span class="i-lucide-x-circle size-4" />
        <span>{{ campaign.failed_today }} failed</span>
      </div>
    </div>

    <!-- Meta Row -->
    <div class="flex items-center gap-4 mb-4 text-xs text-n-slate-10">
      <div>
        <span class="font-medium">{{ t('OMNI.WA_CAMPAIGNS.CARD.LAST_EXECUTED') }}:</span>
        {{ lastExecutedLabel }}
      </div>
      <div>
        <span class="font-medium">{{ t('OMNI.WA_CAMPAIGNS.CARD.NEXT_RUN') }}:</span>
        {{ nextRunLabel }}
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-2 pt-3 border-t border-n-weak">
      <button
        v-if="campaign.status === 'active'"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/50"
        @click="emit('pause')"
      >
        <span class="i-lucide-pause size-3.5" />
        {{ t('OMNI.WA_CAMPAIGNS.CARD.PAUSE') }}
      </button>
      <button
        v-else-if="campaign.status === 'paused' || campaign.status === 'completed'"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50"
        @click="emit('resume')"
      >
        <span class="i-lucide-play size-3.5" />
        {{ campaign.status === 'completed' ? t('OMNI.WA_CAMPAIGNS.CARD.REACTIVATE') : t('OMNI.WA_CAMPAIGNS.CARD.RESUME') }}
      </button>
      <button
        v-if="canEditSection('wa_campaigns')"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 bg-n-alpha-3 border border-n-weak rounded-lg hover:bg-n-alpha-5"
        @click="emit('edit')"
      >
        <span class="i-lucide-pencil size-3.5" />
        {{ t('OMNI.WA_CAMPAIGNS.CARD.EDIT') }}
      </button>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-n-slate-11 bg-n-alpha-3 border border-n-weak rounded-lg hover:bg-n-alpha-5"
        @click="emit('viewLogs')"
      >
        <span class="i-lucide-list size-3.5" />
        {{ t('OMNI.WA_CAMPAIGNS.CARD.VIEW_LOGS') }}
      </button>
      <button
        v-if="canEditSection('wa_campaigns')"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 ml-auto"
        @click="emit('delete')"
      >
        <span class="i-lucide-trash-2 size-3.5" />
        {{ t('OMNI.WA_CAMPAIGNS.CARD.DELETE') }}
      </button>
    </div>
  </div>
</template>
